import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
  TextField,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Add,
  Edit,
  Delete,
  LocalOffer,
  Search,
  Visibility,
  Close,
} from '@mui/icons-material';
import { ModuleLayout } from '../../components/Layout/ModuleLayout';
import PendingApprovalsSection from '../../components/common/PendingApprovalsSection';
import apiClient from '../../api/client';
import { moduleService, moduleServiceService, ModuleService as ModuleServiceType } from '../../services';
import { usePermission } from '../../hooks';
import { colors } from '../../theme/theme';

export const ModuleServicesPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = usePermission();
  const [module, setModule] = useState<any>(null);
  const [services, setServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewServiceName, setPreviewServiceName] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const filteredServices = services.filter((service) => {
    const query = searchQuery.toLowerCase();
    return (
      service.name?.toLowerCase().includes(query) ||
      service.description?.toLowerCase().includes(query) ||
      service.category?.name?.toLowerCase().includes(query)
    );
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [moduleRes, servicesRes] = await Promise.all([
        moduleService.getModule(Number(id)),
        moduleServiceService.getAll(Number(id!)),
      ]);

      if (moduleRes.status === 'success') {
        setModule(moduleRes.data);
      }
      if (servicesRes.status === 'success' && servicesRes.data) {
        const servicesData = servicesRes.data || [];
        setServices(Array.isArray(servicesData) ? servicesData : []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };


  const handleDelete = async (serviceId: number) => {
    if (!window.confirm('Are you sure you want to submit a delete request for this service?')) return;
    try {
      const response = await moduleServiceService.delete(Number(id!), serviceId);
      if (response.status === 'success') {
        setSnackbar({
          open: true,
          message: response.message || 'Delete request submitted for approval',
          severity: 'success',
        });
        setTabValue(1);
        fetchData();
      }
    } catch (err: any) {
      console.error('Failed to delete service:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to delete service',
        severity: 'error',
      });
    }
  };

  const handlePreviewDocument = async (service: any) => {
    setPreviewServiceName(service.name);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewHtml('');
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/admin/county/modules/${id}/services/${service.id}/preview-template`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
            Accept: 'text/html',
          },
        }
      );
      if (response.ok) {
        const html = await response.text();
        setPreviewHtml(html);
      } else {
        const err = await response.json().catch(() => null);
        setPreviewHtml(`<div style="padding:40px;text-align:center;color:#dc2626;"><h3>Preview unavailable</h3><p>${err?.message || 'Failed to load preview'}</p></div>`);
      }
    } catch (err) {
      setPreviewHtml('<div style="padding:40px;text-align:center;color:#dc2626;"><h3>Preview unavailable</h3><p>Failed to connect to server</p></div>');
    } finally {
      setPreviewLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Service Name',
      flex: 1,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ py: 1, whiteSpace: 'normal', lineHeight: 1.4 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {params.value}
          </Typography>
          {params.row.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.4,
              }}
              title={params.row.description}
            >
              {params.row.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value?.name || 'Uncategorized'}
          size="small"
          sx={{ fontWeight: 600, bgcolor: '#eff6ff', color: '#1e40af' }}
        />
      ),
    },
    {
      field: 'billing_type',
      headerName: 'Billing',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ py: 1 }}>
          <Chip
            label={params.value === 'one_off' ? 'One-Off' : 'Periodical'}
            size="small"
            sx={{
              fontWeight: 600,
              bgcolor: params.value === 'one_off' ? '#fef3c7' : '#dbeafe',
              color: params.value === 'one_off' ? '#92400e' : '#1e40af'
            }}
          />
          {params.value === 'periodical' && params.row.billing_cycle && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
              {params.row.billing_cycle.name}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value || 'Active'}
          color="success"
          size="small"
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {params.row.has_printable && (
            <Tooltip title="Preview Document">
              <IconButton
                size="small"
                onClick={() => handlePreviewDocument(params.row)}
                sx={{ color: colors.info.main }}
              >
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {can('services.edit') && (
            <Tooltip title="Edit Service">
              <IconButton
                size="small"
                onClick={() => navigate(`/modules/${id}/services/${params.row.id}/edit`)}
                sx={{ color: colors.primary.main }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {can('services.delete') && (
            <Tooltip title="Delete Service">
              <IconButton
                size="small"
                onClick={() => handleDelete(params.row.id)}
                sx={{ color: '#dc2626' }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <ModuleLayout module={module} moduleId={id!}>
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout module={module} moduleId={id!}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', mb: 1 }}>
              Services
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Manage billable services for this module
            </Typography>
          </Box>
          {can('services.create') && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate(`/modules/${id}/services/create`)}
              sx={{
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Add Service
            </Button>
          )}
        </Box>

        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Services" />
          <Tab label="Approvals" />
        </Tabs>

        {tabValue === 0 && (
          <>
            <TextField
              fullWidth
              placeholder="Search services by name, description, or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
              }}
              sx={{ mb: 3 }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={filteredServices}
                columns={columns}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                disableRowSelectionOnClick
                getRowHeight={() => 'auto'}
                sx={{
                  '& .MuiDataGrid-cell': {
                    display: 'flex',
                    alignItems: 'center',
                    py: 1,
                  },
                  '& .MuiDataGrid-cell:focus': {
                    outline: 'none',
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                  '& .MuiDataGrid-row': {
                    minHeight: '52px !important',
                  },
                }}
                slots={{
                  noRowsOverlay: () => (
                    <Box sx={{ textAlign: 'center', py: 8, mt: 4 }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          bgcolor: colors.primary[50],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 3,
                        }}
                      >
                        <LocalOffer sx={{ fontSize: 40, color: colors.primary.main }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        No Services Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Create your first billable service
                      </Typography>
                      {can('services.create') && (
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => navigate(`/modules/${id}/services/create`)}
                          sx={{
                            background: colors.gradients.primary,
                          }}
                        >
                          Add Service
                        </Button>
                      )}
                    </Box>
                  ),
                }}
              />
            </Box>
          </>
        )}

        {tabValue === 1 && (
          <PendingApprovalsSection
            entityType="service"
            title="Pending Service Approvals"
            onApprovalComplete={fetchData}
          />
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Document Preview Dialog */}
        <Dialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, height: '90vh' } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: colors.primary.main, color: 'white', py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Visibility />
              <Typography variant="h6" component="span">
                Document Preview — {previewServiceName}
              </Typography>
            </Box>
            <IconButton onClick={() => setPreviewOpen(false)} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {previewLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CircularProgress />
              </Box>
            ) : (
              <iframe
                srcDoc={previewHtml}
                title="Document Preview"
                style={{ width: '100%', flex: 1, border: 'none' }}
              />
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, bgcolor: colors.grey[50], borderTop: `1px solid ${colors.grey[200]}` }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
              This is a preview with sample data. Actual documents will contain real business details.
            </Typography>
            <Button onClick={() => setPreviewOpen(false)} variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ModuleLayout>
  );
};
